/**
 * Shared component for displaying workflow execution status badges
 * Used in Test History, Scheduled Tests, Recent Executions, and Execution Details
 */

import { CheckCircle2, XCircle, Circle, CircleDashed, Clock } from 'lucide-react';

interface ExecutionStatusBadgeProps {
  status: string;
  /** Size variant */
  size?: 'sm' | 'md';
}

/**
 * Displays a consistent status badge for workflow executions
 * - completed/success → Passed (green)
 * - failed → Failed (red)
 * - pending/running → Running (blue), static dashed circle
 * - awaiting_human → Waiting (blue), static clock
 *
 * In-progress states use a STATIC icon (no spinner): a run — especially a
 * human step — can stay "Waiting"/"Running" for hours or days, and a spinner
 * wrongly implies work is actively happening and about to finish.
 */
export function ExecutionStatusBadge({ status, size = 'md' }: ExecutionStatusBadgeProps) {
  const sizeClasses = size === 'sm' ? 'gap-1 text-[11px]' : 'gap-1.5 text-[12px]';
  const iconSize = size === 'sm' ? 'h-3 w-3' : 'h-3.5 w-3.5';
  const baseClasses = 'font-body inline-flex items-center whitespace-nowrap font-medium';

  switch (status) {
    case 'completed':
    case 'success':
      return (
        <span className={`${baseClasses} text-status-green ${sizeClasses}`}>
          <CheckCircle2 className={iconSize} />
          Passed
        </span>
      );
    case 'failed':
      return (
        <span className={`${baseClasses} text-status-red ${sizeClasses}`}>
          <XCircle className={iconSize} />
          Failed
        </span>
      );
    case 'inconclusive':
      return (
        <span
          className={`${baseClasses} border-status-orange/30 bg-status-orange-bg text-status-orange ${sizeClasses}`}
        >
          ?
        </span>
      );
    case 'awaiting_human':
      return (
        <span className={`${baseClasses} text-status-blue ${sizeClasses}`}>
          <Clock className={iconSize} />
          Waiting
        </span>
      );
    case 'pending':
    case 'running':
      return (
        <span className={`${baseClasses} text-status-blue ${sizeClasses}`}>
          <CircleDashed className={iconSize} />
          Running
        </span>
      );
    default: {
      const label = status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
      return (
        <span className={`${baseClasses} text-muted-foreground ${sizeClasses}`}>
          <Circle className={iconSize} />
          {label}
        </span>
      );
    }
  }
}
